import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MapPin, Package, ClipboardList, Truck, Trophy, Shield, Globe, Zap, ChevronRight, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useGoogleReviews } from "@/hooks/useGoogleReviews";
import { useQuery } from "@tanstack/react-query";

const TOP_CITIES = [
  { name: "București", slug: "bucuresti" },
  { name: "Cluj-Napoca", slug: "cluj-napoca" },
  { name: "Timișoara", slug: "timisoara" },
  { name: "Iași", slug: "iasi" },
  { name: "Brașov", slug: "brasov" },
  { name: "Constanța", slug: "constanta" },
  { name: "Craiova", slug: "craiova" },
  { name: "Galați", slug: "galati" },
  { name: "Ploiești", slug: "ploiesti" },
  { name: "Oradea", slug: "oradea" },
];

const OCCASIONS = [
  { emoji: "🎂", title: "Ziua de naștere", slug: "ziua-de-nastere", gradient: "from-rose-100 to-secondary" },
  { emoji: "💑", title: "Aniversare cuplu", slug: "aniversare", gradient: "from-red-100 to-rose-50" },
  { emoji: "❤️", title: "Valentine's Day", slug: "valentine", gradient: "from-red-50 to-secondary" },
  { emoji: "🌷", title: "8 Martie", slug: "8-martie", gradient: "from-purple-50 to-secondary" },
  { emoji: "🎄", title: "Crăciun", slug: "craciun", gradient: "from-green-50 to-secondary" },
  { emoji: "👶", title: "Botez", slug: "botez", gradient: "from-blue-50 to-secondary" },
];

const DEFAULT_TESTIMONIALS = [
  {
    name: "Mihaela D.",
    country: "🇬🇧 Londra, UK",
    text: "Am trimis un buchet și tort mamei mele de ziua ei, din Londra, la Cluj. A primit totul perfect, cu confirmare foto. Am plâns și eu și ea. Mulțumesc!",
    occasion: "Ziua de naștere",
    city: "Cluj-Napoca",
    rating: 5,
    initials: "MD",
  },
  {
    name: "Andrei P.",
    country: "🇩🇪 München, Germania",
    text: "Soția mea era singură în Iași de aniversare. Am comandat pachetul Premium și a primit surpriza dimineața. Cel mai bun lucru pe care l-am făcut de când suntem separați de graniță.",
    occasion: "Aniversare cuplu",
    city: "Iași",
    rating: 5,
    initials: "AP",
  },
  {
    name: "Cristina M.",
    country: "🇮🇹 Milano, Italia",
    text: "Simplu, rapid, profesionist. Am găsit partenerul din Brașov, am plătit online și în 2 ore am primit confirmarea livrării cu foto. Recomand tuturor românilor din Italia!",
    occasion: "8 Martie",
    city: "Brașov",
    rating: 5,
    initials: "CM",
  },
];

const POPULAR_COUNTRIES = [
  { code: "gb", name: "UK" },
  { code: "it", name: "Italia" },
  { code: "de", name: "Germania" },
  { code: "es", name: "Spania" },
  { code: "fr", name: "Franța" },
  { code: "be", name: "Belgia" },
  { code: "nl", name: "Olanda" },
  { code: "ca", name: "Canada" },
];

const DEFAULT_FAQ = [
  {
    q: "Cât costă o surpriză la domiciliu în România?",
    a: "Pachetele noastre încep de la 199 RON pentru pachetul Standard, cu opțiuni Premium și Luxury disponibile. Prețul final depinde de localitate (urban vs. rural) și de orarul livrării. Afișăm prețul exact înainte de confirmare.",
  },
  {
    q: "Pot comanda din Italia, UK sau Germania pentru România?",
    a: "Da, platforma noastră este construită special pentru românii de pretutindeni. Poți comanda din orice țară, în orice monedă, și noi ne ocupăm de tot în România. Mii de români din UK, Italia, Germania și Spania folosesc serviciul nostru.",
  },
  {
    q: "În câte ore/zile se livrează o surpriză?",
    a: "Oferim livrare în aceeași zi (pentru comenzi plasate înainte de ora 14:00) și livrare programată la data dorită de tine. Partenerul local confirmă comanda și livrează conform programului stabilit.",
  },
  {
    q: "Livrați și în mediul rural, nu doar în orașe?",
    a: "Da! Acoperim 317 orașe și peste 12.500 de localități rurale din toate cele 42 de județe ale României. Pentru localitățile rurale se aplică un tarif suplimentar per km față de cel mai apropiat partener.",
  },
];

const STEPS = [
  { icon: MapPin, title: "Alegi orașul", desc: "Cauți localitatea destinatarului din toată România." },
  { icon: Package, title: "Selectezi pachetul", desc: "Standard, Premium sau Luxury - pentru orice ocazie." },
  { icon: ClipboardList, title: "Completezi detaliile", desc: "Adresă, dată, mesaj personal - în câteva minute." },
  { icon: Truck, title: "Partenerul livrează", desc: "Confirmare + notificare la finalizare." },
];

const TRUST = [
  {
    icon: Trophy,
    title: "Prima agenție națională de surprize la domiciliu",
    desc: "Singura platformă de surprize la domiciliu care acoperă toate cele 42 de județe ale României - nu doar câteva orașe mari, ci și mii de localități rurale.",
    gradient: "from-yellow-50 to-cream-dark",
    iconBg: "bg-yellow-100",
    iconColor: "text-yellow-700",
  },
  {
    icon: Shield,
    title: "Parteneri locali verificați",
    desc: "Fiecare partener din rețeaua noastră trece printr-un proces de selecție riguros. Livrăm cadouri la domiciliu cu grijă, profesionalism și respect pentru fiecare ocazie specială.",
    gradient: "from-green-50 to-cream-dark",
    iconBg: "bg-green-100",
    iconColor: "text-green-700",
  },
  {
    icon: Globe,
    title: "Comandă surprize din orice țară",
    desc: "Platforma este construită pentru românii de pretutindeni - trimite surprize la domiciliu în România din UK, Italia, Germania, Spania sau oriunde altundeva în lume.",
    gradient: "from-blue-50 to-cream-dark",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-700",
  },
  {
    icon: Zap,
    title: "Livrare confirmată cu notificare",
    desc: "Primești notificare în timp real când surpriza a fost acceptată, când a plecat către destinatar și confirmare foto la livrare. Transparență totală, de la comandă la livrare.",
    gradient: "from-orange-50 to-cream-dark",
    iconBg: "bg-orange-100",
    iconColor: "text-orange-700",
  },
];

// Hook pentru counter animat
function useCountUp(target: number, duration = 1500, startTrigger = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!startTrigger || target === 0) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, startTrigger]);
  return count;
}

// Hook IntersectionObserver simplu
function useInView(threshold = 0.3) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// Componenta counter animat
function AnimatedStat({ value, label, prefix = "", suffix = "" }: { value: number; label: string; prefix?: string; suffix?: string }) {
  const { ref, inView } = useInView(0.2);
  const count = useCountUp(value, 1500, inView);
  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl md:text-5xl font-bold font-playfair tabular-nums">
        {prefix}{inView ? (value >= 1000 ? `${Math.floor(count / 100) / 10}k+` : count) : 0}{suffix}
      </div>
      <div className="text-sm mt-1.5 opacity-80 font-inter">{label}</div>
    </div>
  );
}

// Componenta sectiune fade-up
function FadeSection({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, inView } = useInView(0.15);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

const Index = () => {
  const navigate = useNavigate();
  const { data: googleReviews } = useGoogleReviews();

  const [seoTitle, setSeoTitle] = useState("Surprize la Domiciliu în România | Livrare Națională");
  const [seoDesc, setSeoDesc] = useState("Prima platformă națională de surprize la domiciliu. Comandă din orice țară, livrăm în 317 orașe și 12.500+ localități din România.");

  useSEO({ title: seoTitle, description: seoDesc });

  useEffect(() => {
    supabase.from("site_settings").select("value").eq("key", "seo_page_/").maybeSingle().then(({ data }) => {
      if (data?.value) { try { const p = JSON.parse(data.value); setSeoTitle(p.title); setSeoDesc(p.description); } catch { /* fallback */ } }
    });
  }, []);

  const { data: testimonials = DEFAULT_TESTIMONIALS } = useQuery({
    queryKey: ["homepage-testimonials"],
    queryFn: async () => {
      const { data } = await supabase.from("site_settings").select("value").eq("key", "homepage_testimonials").maybeSingle();
      if (data?.value) { try { return JSON.parse(data.value); } catch { return DEFAULT_TESTIMONIALS; } }
      return DEFAULT_TESTIMONIALS;
    },
    staleTime: 1000 * 60 * 5,
  });

  const { data: faqItems = DEFAULT_FAQ } = useQuery({
    queryKey: ["homepage-faq"],
    queryFn: async () => {
      const { data } = await supabase.from("site_settings").select("value").eq("key", "homepage_faq").maybeSingle();
      if (data?.value) { try { return JSON.parse(data.value); } catch { return DEFAULT_FAQ; } }
      return DEFAULT_FAQ;
    },
    staleTime: 1000 * 60 * 5,
  });

  const { data: socialLinks = [] } = useQuery({
    queryKey: ["social-links"],
    queryFn: async () => {
      const { data } = await supabase.from("site_settings").select("value").eq("key", "social_links").maybeSingle();
      if (data?.value) {
        try {
          const parsed = JSON.parse(data.value);
          return Object.values(parsed).filter((v) => typeof v === "string" && v.length > 0) as string[];
        } catch { return []; }
      }
      return [];
    },
    staleTime: 1000 * 60 * 10,
  });

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "org-jsonld";
    const orgSchema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Surprize la Domiciliu",
      url: "https://surprizeladomiciliu.ro",
      description: "Prima platformă națională de surprize la domiciliu în România. Livrare de cadouri personalizate în toate cele 42 de județe.",
      areaServed: { "@type": "Country", name: "Romania" },
      ...(socialLinks.length > 0 ? { sameAs: socialLinks } : {}),
      knowsAbout: [
        "surprize la domiciliu România",
        "cadouri la domiciliu",
        "livrare surprize România",
        "cadouri personalizate România",
        "buchete flori livrare România",
        "tort la domiciliu",
        "cadouri diaspora România",
        "surprize ziua de nastere",
        "livrare cadouri din UK în România",
        "livrare cadouri din Italia în România",
        "livrare cadouri din Germania în România",
      ],
    };
    // Only add aggregateRating if we have real Google data
    if (googleReviews?.rating && googleReviews?.reviewCount) {
      orgSchema.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: String(googleReviews.rating),
        reviewCount: String(googleReviews.reviewCount),
        bestRating: "5",
      };
    }
    script.text = JSON.stringify([
      orgSchema,
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Surprize la Domiciliu",
        url: "https://surprizeladomiciliu.ro",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://surprizeladomiciliu.ro/comanda-surpriza?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Livrare Surprize la Domiciliu în România",
        provider: {
          "@type": "Organization",
          name: "Surprize la Domiciliu",
        },
        description: "Serviciu de livrare surprize și cadouri personalizate la domiciliu în toată România. Comandă din orice țară, livrare prin parteneri locali verificați.",
        areaServed: { "@type": "Country", name: "Romania" },
        serviceType: "Livrare cadouri la domiciliu",
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "RON",
          lowPrice: "199",
          highPrice: "549",
          offerCount: "3",
        },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Pachete surprize",
          itemListElement: [
            { "@type": "Offer", name: "Pachet Standard", price: "199", priceCurrency: "RON" },
            { "@type": "Offer", name: "Pachet Premium", price: "349", priceCurrency: "RON" },
            { "@type": "Offer", name: "Pachet Luxury", price: "549", priceCurrency: "RON" },
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((faq: { q: string; a: string }) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ]);
    document.head.appendChild(script);
    return () => { document.getElementById("org-jsonld")?.remove(); };
  }, [faqItems, googleReviews, socialLinks]);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["homepage-stats"],
    queryFn: async () => {
      const [countiesRes, citiesRes, villagesRes] = await Promise.all([
        supabase.from("counties").select("id", { count: "exact", head: true }).eq("is_active", true),
        supabase.from("cities").select("id", { count: "exact", head: true }).eq("is_active", true),
        supabase.from("villages").select("id", { count: "exact", head: true }).eq("is_active", true),
      ]);
      return {
        counties: countiesRes.count ?? 42,
        cities: citiesRes.count ?? 317,
        villages: villagesRes.count ?? 12500,
      };
    },
    staleTime: 1000 * 60 * 10,
  });

  return (
    <main className="overflow-x-hidden">

      {/* ══════════════════════════════════════
          1. HERO
      ══════════════════════════════════════ */}
      <section className="relative overflow-hidden py-20 md:py-32 px-4 text-center bg-gradient-hero">
        {/* Pattern SVG subtil */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" aria-hidden>
          <svg width="100%" height="100%">
            <defs>
              <pattern id="hearts" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                <text x="15" y="35" fontSize="24" fill="hsl(351 75% 61%)">♥</text>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hearts)" />
          </svg>
        </div>

        {/* Elemente flotante decorative */}
        <span className="absolute top-16 left-4 md:left-12 text-3xl md:text-5xl animate-float pointer-events-none select-none" aria-hidden>🎁</span>
        <span className="hidden md:block absolute top-24 right-16 text-4xl animate-float-slow pointer-events-none select-none" style={{ animationDelay: "1.5s" }} aria-hidden>🌸</span>
        <span className="absolute top-8 right-4 md:bottom-16 md:top-auto md:left-1/4 md:right-auto text-2xl md:text-3xl animate-float pointer-events-none select-none" style={{ animationDelay: "0.8s" }} aria-hidden>💝</span>
        <span className="hidden lg:block absolute bottom-20 right-1/4 text-4xl animate-float-slow pointer-events-none select-none" style={{ animationDelay: "2s" }} aria-hidden>✨</span>

        <div className="relative max-w-3xl mx-auto space-y-7">
          {/* Credibility pills */}
          <div className="flex flex-wrap justify-center gap-2">
            {[
              { icon: "⭐", text: googleReviews ? `${googleReviews.rating}/5 - ${googleReviews.reviewCount} recenzii Google` : "42 județe acoperite" },
              { icon: "📍", text: "42 județe acoperite" },
              { icon: "📸", text: "Livrare rapidă și sigură" },
            ].filter((pill, i) => !(i === 0 && !googleReviews)).map((pill) => (
              <span key={pill.text} className="glass flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium text-foreground/80">
                <span>{pill.icon}</span>{pill.text}
              </span>
            ))}
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight font-playfair">
            <span className="gradient-text">Surprize</span> la Domiciliu<br className="hidden sm:block" /> în Toată România
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed font-inter">
            Comandă surprize la domiciliu în România din orice colț al lumii. Cadouri personalizate, buchete de flori,
            torturi și coșuri cadou - livrate la domiciliu de parteneri locali verificați în toate cele 42 de județe ale țării.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button
              size="lg"
              className="text-base px-8 bg-gradient-primary border-0 shadow-warm hover:shadow-warm-lg hover:opacity-90 animate-pulse-soft transition-all"
              onClick={() => navigate("/comanda-surpriza")}
            >
              Trimite o surpriză acum →
            </Button>
            <Button size="lg" variant="outline" className="text-base border-primary/30 hover:border-primary hover:bg-secondary" asChild>
              <Link to="/cum-functioneaza">Cum funcționează ↓</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          2. STATS STRIP — counter animat
      ══════════════════════════════════════ */}
      <section className="bg-gradient-primary text-primary-foreground py-12 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {statsLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-2 text-center">
                <Skeleton className="h-12 w-20 mx-auto bg-primary-foreground/20" />
                <Skeleton className="h-4 w-28 mx-auto bg-primary-foreground/20" />
              </div>
            ))
          ) : (
            <>
              <AnimatedStat value={stats?.counties ?? 42} label="Județe acoperite" />
              <AnimatedStat value={stats?.cities ?? 317} label="Orașe cu livrare" />
              <AnimatedStat value={stats?.villages ?? 12500} label="Localități rurale" />
              <AnimatedStat value={100} label="Satisfacție garantată" suffix="%" />
            </>
          )}
        </div>
      </section>

      {/* ══════════════════════════════════════
          3. CUM FUNCȚIONEAZĂ — flow vizual
      ══════════════════════════════════════ */}
      <section className="py-20 px-4 max-w-5xl mx-auto">
        <FadeSection className="text-center space-y-2 mb-12">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary font-inter">Simplu & rapid</p>
          <h2 className="text-3xl md:text-4xl font-bold font-playfair">Cum funcționează</h2>
          <p className="text-muted-foreground font-inter">4 pași simpli, de oriunde din lume</p>
        </FadeSection>

        <div className="relative grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Connector line — desktop */}
          <div className="hidden lg:block absolute top-10 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-primary/20 via-primary/60 to-primary/20 pointer-events-none" aria-hidden />

          {STEPS.map((step, i) => (
            <FadeSection key={i} delay={i * 120} className="relative flex flex-col items-center text-center gap-3 p-6 rounded-2xl border bg-card hover:shadow-warm transition-all duration-300 group">
              {/* Număr mare în background */}
              <span className="absolute top-3 right-4 text-6xl font-bold font-playfair text-primary/5 select-none" aria-hidden>{i + 1}</span>

              <div className="relative w-14 h-14 rounded-2xl bg-gradient-primary flex items-center justify-center text-primary-foreground shadow-warm group-hover:scale-110 transition-transform duration-300">
                <step.icon className="h-6 w-6" />
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-background border-2 border-primary text-primary text-[10px] font-bold flex items-center justify-center font-inter">
                  {i + 1}
                </span>
              </div>
              <div>
                <h3 className="font-semibold font-playfair">{step.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed font-inter">{step.desc}</p>
              </div>
            </FadeSection>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link to="/cum-functioneaza" className="text-sm text-primary hover:underline inline-flex items-center gap-1 font-medium font-inter">
            Detalii complete despre cum funcționează <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* ══════════════════════════════════════
          4. OCAZII POPULARE — carousel mobil
      ══════════════════════════════════════ */}
      <section className="py-20 px-4 bg-muted/50">
        <div className="max-w-5xl mx-auto">
          <FadeSection className="text-center space-y-2 mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-primary font-inter">Personalizat pentru tine</p>
            <h2 className="text-3xl md:text-4xl font-bold font-playfair">Ocazii populare</h2>
            <p className="text-muted-foreground font-inter">Surprize personalizate pentru fiecare moment special</p>
          </FadeSection>

          {/* Desktop grid / Mobile scroll */}
          <div className="flex gap-4 overflow-x-auto snap-x-mandatory pb-4 sm:grid sm:grid-cols-3 lg:grid-cols-6 sm:overflow-x-visible sm:pb-0 scrollbar-hide">
            {OCCASIONS.map((occ, i) => (
              <Link
                key={occ.slug}
                to={`/ocazii/${occ.slug}`}
                className={`snap-start shrink-0 w-[160px] sm:w-auto flex flex-col items-center gap-3 p-5 bg-gradient-to-b ${occ.gradient} rounded-2xl border border-border/50 hover:border-primary/40 hover:shadow-warm hover:-translate-y-2 transition-all duration-300 text-center group`}
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                <span className="text-4xl group-hover:scale-110 transition-transform duration-300 inline-block">{occ.emoji}</span>
                <span className="text-sm font-medium font-inter group-hover:text-primary transition-colors leading-tight">{occ.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          5. ACOPERIRE NAȚIONALĂ
      ══════════════════════════════════════ */}
      <section className="py-20 px-4 max-w-5xl mx-auto">
        <FadeSection className="text-center space-y-2 mb-12">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary font-inter">Prezență națională</p>
          <h2 className="text-3xl md:text-4xl font-bold font-playfair">Livrăm surprize în toată România</h2>
          <p className="text-muted-foreground max-w-xl mx-auto font-inter">
            Indiferent în ce oraș se află familia ta, partenerii noștri locali sunt acolo.
          </p>
        </FadeSection>

        <div className="flex flex-wrap justify-center gap-2.5 mb-8">
          {TOP_CITIES.map((city, i) => (
            <FadeSection key={city.slug} delay={i * 50}>
              <Link
                to={`/surprize-la-domiciliu/${city.slug}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-border bg-card text-sm font-medium hover:bg-primary hover:text-primary-foreground hover:border-primary hover:shadow-warm transition-all duration-200 font-inter"
              >
                <MapPin className="h-3 w-3" />
                {city.name}
              </Link>
            </FadeSection>
          ))}
        </div>
        <div className="text-center">
          <Link to="/orase" className="text-sm text-primary hover:underline inline-flex items-center gap-1 font-medium font-inter">
            → Vezi toate cele {stats?.cities ?? 317} orașe
          </Link>
        </div>
      </section>

      {/* ══════════════════════════════════════
          6. DE CE NOI
      ══════════════════════════════════════ */}
      <section className="py-20 px-4 bg-muted/50">
        <div className="max-w-5xl mx-auto">
          <FadeSection className="text-center space-y-2 mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-primary font-inter">De ce noi</p>
            <h2 className="text-3xl md:text-4xl font-bold font-playfair">Prima platformă națională<br className="hidden sm:block" /> de surprize la domiciliu</h2>
            <p className="text-muted-foreground max-w-xl mx-auto font-inter">
              Nu suntem o agenție locală. Suntem prezenți în toate județele României.
            </p>
          </FadeSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TRUST.map((item, i) => (
              <FadeSection key={item.title} delay={i * 100}>
                <div className={`h-full bg-gradient-to-b ${item.gradient} rounded-2xl border border-border/60 p-6 flex flex-col gap-4 hover:shadow-warm hover:-translate-y-1 transition-all duration-300`}>
                  <div className={`w-12 h-12 rounded-xl ${item.iconBg} flex items-center justify-center ${item.iconColor}`}>
                    <item.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold font-playfair">{item.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed font-inter">{item.desc}</p>
                  </div>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          7. PENTRU ROMÂNII DE PRETUTINDENI
      ══════════════════════════════════════ */}
      <section className="relative py-20 px-4 overflow-hidden bg-foreground text-background">
        {/* Pattern geometric */}
        <div className="absolute inset-0 opacity-5 pointer-events-none" aria-hidden>
          <svg width="100%" height="100%">
            <defs>
              <pattern id="geo" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                <circle cx="40" cy="40" r="30" fill="none" stroke="white" strokeWidth="1" />
                <circle cx="40" cy="40" r="15" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#geo)" />
          </svg>
        </div>

        <div className="relative max-w-4xl mx-auto text-center space-y-8">
          <FadeSection>
            <p className="text-sm font-semibold uppercase tracking-widest text-primary font-inter">Pentru românii de pretutindeni</p>
            <h2 className="text-3xl md:text-4xl font-bold font-playfair mt-3">
              Ești în afara țării?<br className="hidden sm:block" /> Noi suntem acasă.
            </h2>
            <p className="text-background/70 text-lg max-w-2xl mx-auto leading-relaxed font-inter mt-4">
              De la Londra la Roma, de la Madrid la München - mii de români folosesc platforma noastră
              pentru a trimite surprize și cadouri la domiciliu familiei din România. Fie că e ziua mamei,
              aniversarea iubitei sau Crăciunul, cu Surprize la Domiciliu poți fi prezent chiar dacă ești la mii de kilometri distanță.
            </p>
          </FadeSection>

          <div className="flex flex-wrap justify-center gap-3">
            {POPULAR_COUNTRIES.map((c, i) => (
              <FadeSection key={c.name} delay={i * 60}>
                <span className="glass flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium font-inter text-background/90">
                  <img
                    src={`https://flagcdn.com/24x18/${c.code}.png`}
                    srcSet={`https://flagcdn.com/48x36/${c.code}.png 2x`}
                    width={24}
                    height={18}
                    alt={c.name}
                    className="rounded-sm shadow-sm"
                  />
                  {c.name}
                </span>
              </FadeSection>
            ))}
          </div>

          <div>
            <Button
              size="lg"
              className="text-base px-8 bg-gradient-primary border-0 shadow-warm hover:opacity-90 transition-opacity"
              onClick={() => navigate("/comanda-surpriza")}
            >
              Trimite o surpriză acasă →
            </Button>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          8. TESTIMONIALE / RECENZII GOOGLE
      ══════════════════════════════════════ */}
      <section className="py-20 px-4 bg-muted/50">
        <div className="max-w-5xl mx-auto">
          <FadeSection className="text-center space-y-2 mb-12">
            <div className="flex justify-center gap-1 mb-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-primary text-primary" />
              ))}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-playfair">Ce spun clienții noștri</h2>
            {googleReviews ? (
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <p className="text-muted-foreground font-inter">
                  {googleReviews.rating} din 5 stele · {googleReviews.reviewCount} recenzii
                </p>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background border text-xs font-medium text-muted-foreground">
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden>
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Recenzii Google
                </span>
              </div>
            ) : (
              <p className="text-muted-foreground font-inter">Opiniile clienților noștri</p>
            )}
          </FadeSection>

          {/* Google Reviews */}
          {googleReviews && googleReviews.reviews.length > 0 ? (
            <div className="flex gap-5 overflow-x-auto snap-x-mandatory pb-4 md:grid md:grid-cols-3 md:overflow-x-visible md:pb-0">
              {googleReviews.reviews.slice(0, 3).map((r, i) => (
                <div
                  key={`gr-${i}`}
                  className="snap-start shrink-0 w-[85vw] sm:w-[70vw] md:w-auto bg-card rounded-2xl p-6 flex flex-col gap-4 border hover:shadow-warm hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="text-7xl font-playfair leading-none text-primary/10 select-none -mb-4">"</div>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1 font-inter line-clamp-4">"{r.text}"</p>
                  <div className="border-t pt-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground text-sm font-bold font-inter shrink-0">
                      {r.author.split(" ").map(w => w[0]?.toUpperCase() ?? "").join("").slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm font-playfair">{r.author}</div>
                      <div className="text-xs text-muted-foreground font-inter">{r.relativeTime}</div>
                    </div>
                    <div className="flex gap-0.5 shrink-0">
                      {Array.from({ length: r.rating }).map((_, j) => (
                        <Star key={j} className="h-3.5 w-3.5 fill-primary text-primary" />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Fallback: manual testimonials */
            <div className="flex gap-5 overflow-x-auto snap-x-mandatory pb-4 md:grid md:grid-cols-3 md:overflow-x-visible md:pb-0">
              {testimonials.map((t: typeof DEFAULT_TESTIMONIALS[0], i: number) => (
                <div
                  key={t.name}
                  className="snap-start shrink-0 w-[85vw] sm:w-[70vw] md:w-auto bg-card rounded-2xl p-6 flex flex-col gap-4 border hover:shadow-warm hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="text-7xl font-playfair leading-none text-primary/10 select-none -mb-4">"</div>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1 font-inter line-clamp-4">"{t.text}"</p>
                  <div className="border-t pt-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground text-sm font-bold font-inter shrink-0">
                      {t.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm font-playfair">{t.name}</div>
                      <div className="text-xs text-muted-foreground font-inter">{t.country}</div>
                      <div className="text-xs text-muted-foreground font-inter">{t.occasion} · {t.city}</div>
                    </div>
                    <div className="flex gap-0.5 shrink-0">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <Star key={j} className="h-3.5 w-3.5 fill-primary text-primary" />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ══════════════════════════════════════
          9. FAQ
      ══════════════════════════════════════ */}
      <section className="py-20 px-4 max-w-3xl mx-auto">
        <FadeSection className="text-center space-y-2 mb-12">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary font-inter">Ai întrebări?</p>
          <h2 className="text-3xl md:text-4xl font-bold font-playfair">Întrebări frecvente</h2>
          <p className="text-muted-foreground font-inter">Tot ce trebuie să știi înainte de a comanda</p>
        </FadeSection>

        <Accordion type="single" collapsible className="space-y-3">
          {faqItems.map((faq: { q: string; a: string }, i: number) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="border rounded-2xl px-5 bg-card hover:border-primary/30 transition-colors"
            >
              <AccordionTrigger className="text-left font-medium py-5 hover:no-underline font-playfair text-sm md:text-base">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-5 leading-relaxed font-inter">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* ══════════════════════════════════════
          SEO TEXT SECTION
      ══════════════════════════════════════ */}
      <section className="py-16 px-4 max-w-4xl mx-auto">
        <div className="space-y-4 text-muted-foreground leading-relaxed font-inter text-sm">
          <h2 className="text-2xl font-bold text-foreground font-playfair">Surprize la Domiciliu în România - Tot ce trebuie să știi</h2>
          <p>
            <strong className="text-foreground">Surprize la Domiciliu</strong> este prima platformă națională de livrare
            a surprizelor și cadourilor personalizate în România. Acoperim toate cele 42 de județe cu parteneri locali
            verificați, oferind servicii de livrare de <Link to="/pachete" className="text-primary underline hover:no-underline">cadouri la domiciliu</Link>,
            buchete de flori, torturi aniversare, coșuri cadou și surprize personalizate pentru orice ocazie.
          </p>
          <p>
            Platforma noastră este ideală pentru românii din diaspora care doresc să trimită cadouri de la distanță
            în România. Comandă din UK, Italia, Germania, Spania sau orice altă țară, iar partenerii noștri livrează
             surpriza la domiciliu în <Link to="/orase" className="text-primary underline hover:no-underline">peste 317 orașe</Link> și
            12.500+ localități din toată România. Oferim livrare în aceeași zi, livrare programată și surprize
            personalizate pentru <Link to="/ocazii/ziua-de-nastere" className="text-primary underline hover:no-underline">ziua de naștere</Link>,{" "}
            <Link to="/ocazii/aniversare" className="text-primary underline hover:no-underline">aniversări</Link>,{" "}
            <Link to="/ocazii/8-martie" className="text-primary underline hover:no-underline">8 Martie</Link>,{" "}
            <Link to="/ocazii/craciun" className="text-primary underline hover:no-underline">Crăciun</Link> și multe alte ocazii speciale.
          </p>
          <p>
             Descoperă <Link to="/cum-functioneaza" className="text-primary underline hover:no-underline">cum funcționează</Link> platforma
            noastră sau <Link to="/despre-noi" className="text-primary underline hover:no-underline">află povestea noastră</Link>.
            Ești antreprenor local? <Link to="/devino-partener" className="text-primary underline hover:no-underline">Devino partener</Link> și
            intră în cea mai mare rețea de livrare de surprize din România.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════
          10. CTA FINAL
      ══════════════════════════════════════ */}
      <section className="py-20 px-4">
        <FadeSection>
          <div className="max-w-2xl mx-auto relative overflow-hidden rounded-3xl bg-gradient-primary text-primary-foreground text-center p-10 md:p-16 shadow-warm-lg">
            {/* Pattern confetti */}
            <div className="absolute inset-0 opacity-10 pointer-events-none" aria-hidden>
              <svg width="100%" height="100%">
                <defs>
                  <pattern id="confetti" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="5" cy="5" r="2" fill="white" opacity="0.6" />
                    <rect x="20" y="15" width="4" height="4" rx="1" fill="white" opacity="0.4" transform="rotate(30 22 17)" />
                    <circle cx="35" cy="30" r="1.5" fill="white" opacity="0.5" />
                    <rect x="10" y="28" width="3" height="3" rx="0.5" fill="white" opacity="0.3" transform="rotate(-20 11 29)" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#confetti)" />
              </svg>
            </div>

            <div className="relative space-y-5">
              <div className="text-sm font-semibold opacity-80 font-inter">
                {googleReviews ? `⭐⭐⭐⭐⭐ ${googleReviews.rating}/5 din ${googleReviews.reviewCount} recenzii Google` : "⭐⭐⭐⭐⭐ Satisfacție garantată"}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold font-playfair">Gata să trimiți o surpriză?</h2>
              <p className="text-primary-foreground/80 text-lg leading-relaxed font-inter">
                Fă o persoană dragă fericită astăzi. Durează mai puțin decât crezi - și contează mai mult decât orice mesaj.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Button
                  size="lg"
                  className="text-base px-8 bg-background text-primary hover:bg-background/90 transition-colors font-semibold"
                  onClick={() => navigate("/comanda-surpriza")}
                >
                  Trimite o surpriză acum →
                </Button>
                <Button size="lg" variant="outline" className="text-base border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10" asChild>
                  <Link to="/pachete">Vezi pachetele →</Link>
                </Button>
              </div>
            </div>
          </div>
        </FadeSection>
      </section>
    </main>
  );
};

export default Index;
